/**
 * Browserfähigkeiten, die einen **sicheren Kontext** voraussetzen.
 *
 * `crypto.randomUUID()` und `navigator.clipboard` stehen nur unter HTTPS oder auf `localhost` zur
 * Verfügung. Nach §34.3 läuft die Anwendung im Betrieb hinter einem Reverse-Proxy mit TLS — im
 * lokalen Netz wird sie aber ausdrücklich auch über `http://<IP>:3250` geprüft (§34.3,
 * `APP_BIND_ADDRESS`), und dort fehlen beide.
 *
 * Ohne Rückfall geschah dabei das Schlimmste, was eine Oberfläche tun kann: Der Aufruf warf im
 * Klick-Handler, React verschluckte den Fehler, und die Schaltfläche wirkte tot — bei der
 * Stimmabgabe war so von diesen Geräten aus **gar keine** Stimme möglich.
 *
 * Deshalb liegen beide Zugriffe hier gebündelt: Wer eine dieser Fähigkeiten braucht, bekommt sie
 * über diese Funktionen und nie unmittelbar aus dem globalen Objekt.
 */

/**
 * Eine Kennung im UUID-v4-Format.
 *
 * Bevorzugt `crypto.randomUUID()`. Fehlt es, wird die Kennung aus `crypto.getRandomValues()`
 * gebildet — das ist **nicht** auf sichere Kontexte beschränkt und liefert dieselbe Güte an
 * Zufall. Erst wenn auch das fehlt, greift `Math.random()`.
 *
 * Verwendet für die Vorgangskennung der Stimmabgabe (§13.3). Deren Zweck ist die Erkennung einer
 * Wiederholung, nicht Geheimhaltung: Der Server prüft sie ausschließlich je Abstimmung und Token.
 */
export function erzeugeKennung(): string {
  const c: Crypto | undefined = globalThis.crypto;

  if (typeof c?.randomUUID === 'function') {
    return c.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Version 4 und Variante 1 nach RFC 4122 — dieselbe Gestalt wie bei `randomUUID()`.
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (wert) => wert.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

/**
 * Kopiert Text in die Zwischenablage. Gibt zurück, ob es gelungen ist.
 *
 * Ohne sicheren Kontext fehlt `navigator.clipboard`; dann wird der überholte Weg über ein
 * unsichtbares Textfeld und `document.execCommand('copy')` versucht. Er ist abgekündigt, aber in
 * allen Zielbrowsern noch vorhanden — und ein stiller Fehlschlag wäre hier besonders ärgerlich,
 * weil die Klartext-Ausgabe nach §7.9 nicht beliebig oft abgerufen werden soll.
 */
export async function kopiereText(text: string): Promise<boolean> {
  if (navigator.clipboard !== undefined) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Kann auch im sicheren Kontext an einer Berechtigung scheitern — dann der Rückfall.
    }
  }

  const feld = document.createElement('textarea');
  feld.value = text;
  feld.setAttribute('readonly', '');
  feld.style.position = 'fixed';
  feld.style.opacity = '0';
  document.body.appendChild(feld);

  try {
    feld.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(feld);
  }
}
